/* Luxuntique 2026 — "Your size · saved"
   1. Saves the shopper's size (this browser only) when they choose one on a product
      page: the stock size picker (lux-size-picker) or a pre-order size button (.lx-po__size).
   2. <lux-saved-size> on collection and search pages shows that size with a switch
      (snippets/lux-size-lock.liquid). With no size yet it reuses Find my size answers
      (window.LuxFit, assets/lux-size-finder.js), or invites the shopper to Find my size.
      The switch ticks or unticks the matching value in the theme's own Size filter,
      so the normal filter update runs. It turns on automatically the first time a
      saved size can be applied, and remembers if the shopper turns it off. */

import { StandardEvents } from '@shopify/events';

const SIZE_KEY = 'lux_size_v1';
const PREF_KEY = 'lux_size_filter';
const SIZE_PARAM = 'filter.v.t.shopify.shoe-size';

/** @returns {{eu: string, uk?: string} | null} */
function readSize() {
  try {
    const value = JSON.parse(localStorage.getItem(SIZE_KEY) || 'null');
    return value && value.eu ? value : null;
  } catch {
    return null;
  }
}

/** @param {{eu: string, uk?: string}} size */
function writeSize(size) {
  try {
    localStorage.setItem(SIZE_KEY, JSON.stringify({ eu: String(size.eu), uk: size.uk ? String(size.uk) : '', at: Date.now() }));
  } catch {
    // Storage blocked: the switch simply won't appear.
  }
}

/** @returns {'on' | 'off' | null} */
function readPref() {
  try {
    const value = localStorage.getItem(PREF_KEY);
    return value === 'on' || value === 'off' ? value : null;
  } catch {
    return null;
  }
}

/** @param {'on' | 'off'} value */
function writePref(value) {
  try {
    localStorage.setItem(PREF_KEY, value);
  } catch {}
}

/** Forget the size and the Find my size answers it can come from. */
function forget() {
  try {
    localStorage.removeItem(SIZE_KEY);
    localStorage.removeItem(PREF_KEY);
    localStorage.removeItem('lux_fit_v1');
  } catch {}
}

/** EU number as text without trailing zeros, e.g. "42", "42.5". */
function euText(value) {
  const number = parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(number) ? String(value).trim() : String(number);
}

/* ---------- 1. Save from product pages ---------- */

if (!window.__luxSavedSizeListeners) {
  window.__luxSavedSizeListeners = true;

  // Stock products: the size picker radios (relabelled UK/EU by lux-product.js)
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const fieldset = input.closest('fieldset.lux-size-picker');
    if (!fieldset) return;
    const ukText = input.closest('label')?.querySelector('.lux-v-main')?.textContent || '';
    writeSize({ eu: euText(input.value), uk: ukText.replace(/^UK\s*/i, '').trim() });
  });

  // Find my size: "Use this size" on a product page
  document.addEventListener('lux:size-apply', (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (detail && detail.eu) writeSize({ eu: euText(detail.eu), uk: detail.uk ? String(detail.uk) : '' });
  });

  // Pre-order products: the size buttons
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('.lx-po__size') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.eu) return;
    writeSize({ eu: euText(button.dataset.eu), uk: button.dataset.uk || '' });
  });
}

/* ---------- 2. Collection switch ---------- */

/** No size saved yet, but the shopper answered Find my size: use its general recommendation. */
function sizeFromFinder() {
  const fit = /** @type {any} */ (window).LuxFit;
  if (!fit) return null;
  const profile = fit.load();
  if (!profile) return null;
  try {
    const result = fit.recommend(profile, null);
    if (!result || !result.rec || !result.rec.eu) return null;
    writeSize({ eu: euText(result.rec.eu), uk: result.rec.uk ? String(result.rec.uk) : '' });
    return readSize();
  } catch {
    return null;
  }
}

let autoApplied = false; // one switch may sit in the sidebar and one above the grid

class LuxSavedSize extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.querySelector('[data-toggle]')?.addEventListener('click', this.#toggle, { signal });
    this.querySelector('[data-forget]')?.addEventListener('click', this.#forget, { signal });
    for (const link of this.querySelectorAll('[data-finder-link]')) {
      link.addEventListener('click', () => this.#addReturn(/** @type {HTMLAnchorElement} */ (link)), { signal });
    }

    const onUpdate = async (/** @type {Event & {promise?: Promise<unknown>}} */ event) => {
      if (event.promise) {
        try {
          await event.promise;
        } catch {
          return;
        }
      }
      this.sync();
    };
    document.addEventListener(StandardEvents.collectionUpdate, onUpdate, { signal });
    document.addEventListener(StandardEvents.searchUpdate, onUpdate, { signal });
    document.addEventListener('lux:fit-change', () => this.sync(), { signal });

    this.sync();
    this.#maybeAutoApply();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * The theme's Size filter checkbox for the saved size, if this page has one.
   * Desktop uses the sidebar form, phones the filter drawer form.
   */
  #input() {
    const size = readSize();
    if (!size) return null;
    const wanted = euText(size.eu);
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    /** @type {HTMLInputElement[]} */
    const matches = [];
    for (const input of document.querySelectorAll(`input[type="checkbox"][name="${SIZE_PARAM}"]`)) {
      if (!(input instanceof HTMLInputElement)) continue;
      const label = input.dataset.label || input.getAttribute('aria-label') || '';
      if (euText(label) === wanted) matches.push(input);
    }
    const inDrawer = (/** @type {HTMLInputElement} */ input) => Boolean(input.closest('.facets--drawer'));
    return matches.find((input) => inDrawer(input) !== desktop) || matches[0] || null;
  }

  /** Wait until the theme's filter components can react to the checkbox. */
  async #filtersReady() {
    await Promise.all([
      customElements.whenDefined('facet-inputs-component'),
      customElements.whenDefined('facets-form-component'),
    ]);
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  sync() {
    const size = readSize() || sizeFromFinder();
    if (!size) {
      this.toggleAttribute('data-on', false);
      this.dataset.state = 'empty';
      return;
    }

    const label = size.uk ? `UK ${size.uk} · EU ${euText(size.eu)}` : `EU ${euText(size.eu)}`;
    const sizeEl = this.querySelector('[data-size]');
    if (sizeEl) sizeEl.textContent = label;

    const input = this.#input();
    if (!input || (input.disabled && !input.checked)) {
      this.toggleAttribute('data-on', false);
      this.dataset.state = 'unavailable';
      return;
    }

    const on = input.checked;
    this.querySelector('[data-toggle]')?.setAttribute('aria-checked', on ? 'true' : 'false');
    this.toggleAttribute('data-on', on);
    this.dataset.state = 'saved';
  }

  /** Find my size links bring the shopper back here (without the old size filter) with their new size
   * @param {HTMLAnchorElement} link */
  #addReturn(link) {
    const here = new URL(window.location.href);
    here.searchParams.delete(SIZE_PARAM);
    here.searchParams.delete('page');
    const target = new URL(link.getAttribute('href') || '/pages/size-guide?find=1', window.location.origin);
    target.searchParams.set('return', here.pathname + here.search);
    if (this.dataset.from) target.searchParams.set('from', this.dataset.from);
    link.href = target.pathname + target.search;
  }

  /** First visit with a saved size: apply it once, unless the shopper turned it off before. */
  async #maybeAutoApply() {
    if (autoApplied || readPref() === 'off') return;
    await this.#filtersReady();
    if (autoApplied) return;
    const input = this.#input();
    if (!input || input.checked || input.disabled) return;
    autoApplied = true;
    writePref('on');
    input.click();
  }

  #toggle = async () => {
    await this.#filtersReady();
    const input = this.#input();
    if (!input) return;
    writePref(input.checked ? 'off' : 'on');
    input.click();
  };

  #forget = async () => {
    await this.#filtersReady();
    const input = this.#input();
    forget();
    if (input?.checked) input.click();
    for (const lock of document.querySelectorAll('lux-saved-size')) {
      if (lock instanceof LuxSavedSize) lock.sync();
    }
  };
}

if (!customElements.get('lux-saved-size')) {
  customElements.define('lux-saved-size', LuxSavedSize);
}
